import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function useNews() {
  const [news, setNews] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchNews() {
      try {
        setLoading(true)
        setError(null)

        const { data, error: fetchError } = await supabase
          .from('news')
          .select('*')
          .eq('published', true)

        if (fetchError) {
          throw fetchError
        }

        // Transform the data to match the existing structure
        const transformedNews = data.map(item => ({
          title: item.title,
          date: item.date,
          content: item.content || '',
          image: item.image ? (item.image.startsWith('http') ? item.image : process.env.PUBLIC_URL + item.image) : '',
          link: `/news/${item.slug || item.id}`, // Auto-generate link from slug or ID
          slug: item.slug,
          orderPriority: item.order_priority,
          id: item.id,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }))

        // Helper function to parse date strings like "January 15th 2025" or "Jan 15th 2025"
        const parseNewsDate = (dateStr) => {
          try {
            // Remove ordinal suffixes (st, nd, rd, th)
            const cleaned = dateStr.replace(/(\d+)(st|nd|rd|th)/g, '$1')
            const parsed = new Date(cleaned)
            // If valid date, return it; otherwise return a very old date
            return isNaN(parsed.getTime()) ? new Date(0) : parsed
          } catch {
            return new Date(0) // Return epoch if parsing fails
          }
        }

        // Sort function: order_priority first (nulls last), then by date (newest first)
        const sortNews = (a, b) => {
          // If both have order_priority, sort by that
          if (a.orderPriority != null && b.orderPriority != null) {
            return a.orderPriority - b.orderPriority
          }
          // If only one has order_priority, it comes first
          if (a.orderPriority != null) return -1
          if (b.orderPriority != null) return 1
          // Otherwise, sort by date field (newest first)
          const dateA = parseNewsDate(a.date)
          const dateB = parseNewsDate(b.date)
          return dateB - dateA // Descending order (newest first)
        }

        setNews(transformedNews.sort(sortNews))
      } catch (err) {
        console.error('Error fetching news:', err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchNews()
  }, [])

  return { news, loading, error }
}
